/**
 * @fileoverview Own transient preview-image loading/failure state for document surfaces. The parent supplies the preview source and document identity; `DocumentFileTypeIcon.js` supplies fallback presentation. Image failure does not establish that original content is missing or that a newer revision should be substituted.
 */

import React, { useEffect, useRef, useState } from 'react';
import AppIcon from '../shared/AppIcon';
import DocumentFileTypeIcon from './DocumentFileTypeIcon';
import './DocumentPreviewImage.css';



function DocumentPreviewImage({
  document,
  source,
  retry = false,
  retryLimit = 60,
  retryDelay = 2000,
  className = '',
}) {
  const filename = document?.filename || document?.name || 'Untitled document';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState('loading');
  const retryTimerRef = useRef(null);

  useEffect(() => {
    setAttempt(0);
    setState(source ? 'loading' : 'unavailable');
    return () => {
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
    };
  }, [source]);

  const attemptedSource = source && retry
    ? `${source}${source.includes('?') ? '&' : '?'}preview_attempt=${attempt}`
    : source;
  const handleError =

  () => {
    if (retry && attempt < retryLimit) {
      setState('loading');
      if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = window.setTimeout(() => {
        setAttempt((currentAttempt) => currentAttempt + 1);
      }, retryDelay);
      return;
    }
    setState('unavailable');
  };

  return (
    <div className={`document-preview-image is-${state} ${className}`.trim()}>
      {attemptedSource && (
        <img
          className={state === 'ready' ? 'is-ready' : ''}
          src={attemptedSource}
          alt={`Preview of ${filename}`}
          onLoad={() => {
            if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
            setState('ready');
          }}
          onError={handleError} />
      )}
      {state !== 'ready' && (
        <span className="document-preview-image__status" role="status">
          {state === 'loading'
            ? <AppIcon icon="pi pi-spin pi-spinner" aria-hidden="true" />
            : <DocumentFileTypeIcon file={document} aria-hidden="true" />}
          <strong>{state === 'loading' ? 'Loading preview' : 'Preview unavailable'}</strong>
        </span>
      )}
    </div>
  );
}

export default DocumentPreviewImage;
